import React, { useState, useEffect, useCallback, useContext } from "react";
import Plot from 'react-plotly.js'
import "./MetricChart.css";
import { handleDateChange } from "../../util/handleDateChange"
import { Button, Loading, SingleDatePicker, Select, Option, Checkbox, Modal, Table, THead, TR, TH, TBody, TD, Tooltip, Text } from "@sede-enterprise/shell-ds-react-framework";
import { BarChartSquare, Download, LineChartSolid, Open, QuestionMarkCircle } from "@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components";
import momentGenerateConfig from 'rc-picker/lib/generate/moment'
import { downloadAsExcel, formatDate, getMinMaxFromGraphData } from "../../util/helperFunctions";
import { UnitContext } from "../../App";


const RANGE_SELECTOR_BUTTONS = {
  buttons: [
    {
      step: "day",
      stepmode: "backward",
      count: 14,
      label: "2 Weeks"
    },
    {
      step: "month",
      stepmode: "backward",
      count: 2,
      label: "2 Months"
    },
    {
      step: "year",
      stepmode: "backward",
      count: 1,
      label: "1 Year"
    },
    {
      step: "all",
      label: "All"
    }
  ]
};

import * as R from "rambda";

export const downloadAsXLSX = async (metricData, corrosionLoopID, sensorData, context) => {
  if (
    metricData?.length &&
    metricData[0]?.x?.length
  ) {

    const dates = metricData.map((i) => i.x).flat(1);
    const orderedUniqueDates = Array.from(new Set(dates).values()).sort((a, b) => new Date(a) - new Date(b));
    const downloadData = orderedUniqueDates.map((value) => {
      return metricData.reduce(
        (prev, curr) => {
          return {
            ...prev,
            [curr?.name === "Predicted wall thickness" || curr?.name === "Measured wall thickness" ? `${curr?.name}${context.units ? '[inch]' : '[mm]'}` : `${curr?.name}${curr.text}`]: curr.y[curr.x.indexOf(value)]
          };
        },
        { timestamp: value }
      );
    });

    const Heading = corrosionLoopID ? [["Corrosion Loop", corrosionLoopID]] : [
      ["Site", R.path(["site"], sensorData)],
      ["Production Unit", R.path(["productionUnit"], sensorData)],
      [
        "Unit",
        R.path(
          ["unit"],
          sensorData
        ),
      ],
      [
        "Equipment",
        R.path(["equipment"], sensorData),
      ],
      ["Corrosion Loop", R.path(["corrosionLoop"], sensorData)],
      ["CML", R.path(["cmLocationName"], sensorData)],
      ["Sensor", R.path(["isRealSensor"], sensorData) === true ? R.path(["name"], sensorData) : "No Sensor => This CML has no Sensor installed"],
    ];

    const sheets = [
      { sheetName: 'MetricData', data: downloadData }
    ];


    await downloadAsExcel(sheets, 'MetricData.xlsx', Heading);
  }
};

export const downloadAnalysisAsXLSX = async (metricData, analyisSummary) => {
  if (
    metricData?.length &&
    metricData[0]?.x?.length &&
    analyisSummary?.length
  ) {

    const dates = metricData.map((i) => i.x).flat(1);
    const orderedUniqueDates = Array.from(new Set(dates).values()).sort((a, b) => new Date(a) - new Date(b));

    //get data by featureName for each feature for different sheets of the XLSX file;
    const featureNames = metricData.map((i) => i.featureName);
    const uniqueFeatureNames = Array.from(new Set(featureNames).values());

    //get data by feature for sheets 
    const featureData = uniqueFeatureNames.map((feature) => {
      const featureData = metricData.filter((i) => i.featureName === feature);
      let unit = featureData[0]?.symbol || ""; // Extract the unit (symbol)
      if (unit && unit.includes("/")) {
        unit = unit.replace(/\//g, "_");
      }

      const sheetName = `${unit} ${feature}`; // Combine feature name and unit

      const downloadData = orderedUniqueDates.map((value) => {
        return featureData.reduce(
          (prev, curr) => {
            return {
              ...prev,
              [`${curr.text}`]: curr.y[curr.x.indexOf(value)]
            };
          },
          { timestamp: value }
        );
      });
      return { sheetName, data: downloadData }
    });



    const sheets = [{ sheetName: "Summary", data: analyisSummary }, ...featureData];

    await downloadAsExcel(sheets, 'AnalysisData.xlsx');


  }
}

const MetricChart = ({
  data,
  missing,
  missingData,
  metricData,
  sensorData,
  corrosionLoopID = null,
  loading,
  start,
  setStart,
  end,
  setEnd,
  interval,
  setSelectedInterval,
  intervalOpts,
  forecastMonths,
  setSelectedMonths,
  forecastMonthsOpts,
  isForecastWallThickness,
  lifeExpectancyInYears,
  yaxes = {},
  formDirty,
  onSubmit,
  onClick,
  scaled,
  setScaled,
  isRCA,
  scaledEnabled,
  uxOption,
  setUxOption,
  isReset,
  fixedYRange,
  noLegends,
  plotLoading,
  isAnalysisData,
  analyisSummary
}) => {
  const [revisionCount, setRevisionCount] = useState(0);
  const [annotationsArray, setAnnotationsArray] = useState([]);
  const [startErrorText, setStartErrorText] = useState(null);
  const [inValidStartDate, setInValidStartDate] = useState(null);

  const [endErrorText, setEndErrorText] = useState(null);
  const [inValidEndDate, setInValidEndDate] = useState(null);
  const context = useContext(UnitContext);

  const [openMissingModal, setOpenMissingModal] = useState(false);
  const handleMissingModalClose = () => {
    setOpenMissingModal(false);
  };

  const [chartState, setChartState] = useState({
    data: data,
    layout: {
      autosize: true,
      margin: {
        l: 40,
        r: 40,
        b: 20,
        t: 20,
        pad: 5
      },
      xaxis: {
        automargin: true,
        type: "date",
        rangeselector: RANGE_SELECTOR_BUTTONS,
        domain: scaled ? [0.15, 1] : [0, 1],
        hoverformat: "%e %b %Y, %H:%M"
      },
      ...yaxes,
      showlegend: !noLegends,
      legend: {
        orientation: "h",
        y: -0.2
      },
      hoverdistance: 1,
      hovermode: "x unified",
      annotations: annotationsArray.length ? [...annotationsArray] : []
    },
    frames: [],
    config: {}
  });



  const forecastMonth = forecastMonths && forecastMonths.value ? (
    <div className="forecast-interval-select">
      <p className="data-label">Forecast Months</p>
      <Select value={forecastMonths.value} onChange={value => setSelectedMonths(value)} allowClear={false}>
        {
          forecastMonthsOpts.map(opt => <Option key={opt.value} value={opt.value}>{opt.label}</Option>)
        }
      </Select>
    </div>
  ) : null;

  const round = (number, digits) => {
    if (number === 0) {
      return 0;
    }
    else {
      return Math.round(number * (Math.pow(10, digits))) / Math.pow(10, digits) || "NA";
    }
  };

  const lifeExpectancy = (
    <div className="life-expectancy">
      <p className="data-label">Expected year for End of Life: {round(lifeExpectancyInYears, 0)}</p>
    </div>
  );

  const _setUxOption = option => {
    setUxOption(option);
  };

  const uxOptions = (

    uxOption ? (
      <div style={{ padding: "12px" }}>
        <div className="ux-options-holder">
          <Button
            aria-label="icon"
            id="button-icon"
            disabled={uxOption === "DEFAULT"}
            onClick={() => {
              _setUxOption("DEFAULT");
            }}
            inversed
            data-testid="ux-select-control-default"
            title="Do not show scatter plot and histogram"
          >
            <BarChartSquare />
          </Button>
          <Button
            aria-label="icon"
            disabled={uxOption === "SIDE"}
            onClick={() => {
              _setUxOption("SIDE");
            }}
            inversed
            data-testid="ux-select-control-side"
            title="Shows scatter plot, histogram on the side"
          >
            <LineChartSolid />
          </Button>
          <Button
            aria-label="icon"
            disabled={uxOption === "POPUP"}
            onClick={() => {
              _setUxOption("POPUP");
            }}
            inversed
            data-testid="ux-select-control-popup"
            title="Shows scatter plot, histogram in a popup"
          >
            <Open />
          </Button>
        </div>
      </div>
    ) :
      null

  );

  const loadingScreen = (
    <div className="loading-container chart-loading" role="status">
      <Loading />
    </div>
  );

  const handleClick = event => {
    // This would validate if the clicked point has some predicted value and then allowedClick parameter is set to true
    const filterEvent = event.points.filter(obj => (obj.data.name === "Measured corrosion rate" || obj.data.name === "Predicted corrosion rate"));
    if (filterEvent && filterEvent.length > 0) {
      const dateData = filterEvent[0].x
      onClick(dateData);
    }
  };
  const filteredData = data?.filter((k) => k.yaxis !== 'y2');


  const { min: minValue, max: maxValue } = getMinMaxFromGraphData(filteredData);



  const updateChartData = useCallback(
    dataCopy => {
      const newChartState = { ...chartState };
      const sorted = dataCopy ? [...dataCopy].sort((a, b) => {
        if (a.name === "Predicted wall thickness") return 1;
        if (b.name === "Predicted wall thickness") return -1;
        return 0;
      }) : dataCopy;
      newChartState.data = sorted;
      if (scaled) {
        newChartState.layout.xaxis.domain = [0.05, 1];
      } else {
        newChartState.layout.xaxis.domain = [0, 1];
      }

      newChartState.layout.yaxis = {
        fixedrange: false,
        side: "left",
        range: [minValue - 1, maxValue + 1],
      }

      setChartState(newChartState);
    },
    // eslint-disable-next-line
    [setChartState, scaled, maxValue, minValue]
  );

  useEffect(() => {
    if (isReset === true) {
      const newChartState = { ...chartState };

      var annotationArray = [];
      annotationArray = newChartState.layout.annotations || [];

      setAnnotationsArray([]);

      newChartState.layout.annotations = [];
      setRevisionCount(annotationArray.length + 1);

      setChartState(newChartState);
    }
    // eslint-disable-next-line
  }, [isReset]);

  useEffect(() => {
    const dataCopy = data;
    updateChartData(dataCopy);
  }, [data, updateChartData]);


  const chart = (
    <Plot
      onInitialized={figure => setChartState(figure)}
      onUpdate={figure => setChartState(figure)}
      data={chartState.data}
      layout={chartState.layout}
      onClick={handleClick}
      useResizeHandler={true}
      revision={revisionCount}
      className="full-size-chart"
    />
  );


  const chartControls = (
    <div className="metric-chart-controls-inner">
      <div className="start-picker-wrapper">
        <p className="data-label">Start Date</p>
        <SingleDatePicker
          generateConfig={momentGenerateConfig}
          config="custom"
          format="DD/MM/yyyy"
          value={!inValidStartDate && start ? start : inValidStartDate ? inValidStartDate : null}
          onChange={(date) => {
            setStartErrorText(null)
            setInValidStartDate(null)
            if (date.isBefore(end)) {
              handleDateChange(date, start, setStart, "DD/MM/yyyy")
            }
            else if (date.isAfter(end)) {
              setStartErrorText("Start date cannot be after end date")
              setInValidStartDate(date)
            }
          }
          }
          allowClear={false} />
        <p className="data-label error-label">{startErrorText}</p>
      </div>

      <div className="end-picker-wrapper">
        <p className="data-label">End Date</p>
        <SingleDatePicker
          generateConfig={momentGenerateConfig}
          config="custom"
          format="DD/MM/yyyy"
          value={!inValidEndDate && end ? end : inValidEndDate ? inValidEndDate : null}
          onChange={(date) => {
            setEndErrorText(null)
            setInValidEndDate(null)
            if (date.isAfter(start)) {
              handleDateChange(date, end, setEnd, "DD/MM/yyyy")
            }
            else if (date.isBefore(start)) {
              setEndErrorText("End date cannot be before start date")
              setInValidEndDate(date)
            }
          }
          }
          allowClear={false} />
        <p className="data-label error-label">{endErrorText}</p>
      </div>

      <div className="interval-select-wrapper">
        <p className="data-label">Averaging Interval</p>
        <Select value={interval.value}
          onChange={value => setSelectedInterval(value)}
          allowClear={false}
        >
          {
            intervalOpts.map(opt => <Option key={opt.value} value={opt.value}>{opt.label}</Option>)
          }

        </Select>
      </div>
      <div className="missing-features-wrapper">
        <p className="data-label hidden-label">Missing Features</p>
        {missing && <Tooltip className='tooltip' placement='bottom' overlay={<div className='tooltip-content'>Missing Features</div>} trigger={
          <Button iconOnly icon={<QuestionMarkCircle />} onClick={() => setOpenMissingModal(true)} />
        } />}
      </div>

      {isForecastWallThickness && forecastMonth}

      {/* The help-popup was disabled sinse there is no info necessary */}
      {scaledEnabled ? (
        <Checkbox
          key="scaled"
          withFormControl
          label="Normalize Feature Data"
          checked={scaled}
          onChange={() => {
            setScaled(!scaled);
          }}
        />
      ) : null}
    </div>
  );

  return (
    <div className="column metric-chart-outer-container">
      {/* {loading ? loadingScreen : null} */}
      <div className="row metric-chart-controls-outer">
        {chartControls}

        {uxOptions}
        <div className="metric-chart-side-div">
          {isForecastWallThickness && lifeExpectancy}
          <Tooltip id='home-id-5' overlay={<span id='home-id-6'>Download</span>}
            placement='bottom'
            trigger={<Button iconOnly icon={<Download />}
              // onClick={isAnalysisData ? downloadAnalysisAsXLSX : downloadAsXLSX}
              onClick={() => {
                if (isAnalysisData) {
                  downloadAnalysisAsXLSX(metricData, analyisSummary);
                } else {
                  downloadAsXLSX(metricData, corrosionLoopID, sensorData, context);
                }
              }
              }
              disabled={plotLoading}
              data-testid="download-as-csv-button"
              aria-label="download" />} />

        </div>
      </div>
      {/* This had to be done to make sure that the chart resizes everytime the parent div size changes
      AutoSize option, and responsive option only consider window resizes.
      In one of the Plotly support forums, they confirmed that as of now, there is no other option for parent div resize */}

      {loading && loadingScreen}
      {!loading && uxOption === "SIDE" ? chart : null}
      {!loading && uxOption !== "SIDE" ? chart : null}
      <Modal
        open={openMissingModal}
        title="Missing Features"
        description={`Last Updated On: ${
          missingData &&
          missingData['Features'] &&
          Object.keys(missingData['Features']).length > 0
            ? formatDate(new Date(missingData['Last Updated'][0]))
            : 'NA'
        }`}
        onClose={() => {
          handleMissingModalClose();
        }}
      >
        <Table className='basic-table' style={{ width: "100%" }}>
          <THead style={{ background: "rgb(245, 245, 245)" }}>
            <TR>
              <TH>Features</TH>
              <TH>Source</TH>
              <TH><div className="table-date-header">Last Recieved Date</div></TH>
            </TR>
          </THead>
          <TBody>
            {missingData?.['Features'] && Object.keys(missingData['Features']).length > 0 ? (
                Object.keys(missingData['Features']).map((key, index) => (
                  <TR key={index}>
                    <TD>{missingData['Features'][key]}</TD>
                    <TD>{missingData['Source'][key]}</TD>
                    <TD>
                      {missingData['Latest Timestamp'][key] === "NIPM"
                        ? "NA"
                        : formatDate(new Date(missingData['Latest Timestamp'][key]))}
                    </TD>
                  </TR>
                ))
              ) : (
                <Text>No Missing Features</Text>
              )}
          </TBody>
        </Table>
      </Modal>
    </div>
  );
}

export default MetricChart;
